import { Component, DestroyRef, ElementRef, EventEmitter, inject, input, Input, Output, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { concatMap, finalize, of, tap, throwError } from 'rxjs';
import { CalendarEventsRequest } from 'src/app/models/calendar-events-request.interface';
import { UnitOperationsState } from 'src/app/models/unit-operations-state.model';
import { SettlementsService } from 'src/app/services/settlements.service';
import { IconsService } from 'src/app/shared/services/icons.service';

@Component({
  selector: 'app-modal-unit',
  template: `
    <ng-template #modalUnit let-modal>
      <div class="modal-header">
        <h5 class="modal-title">Departamento/Local</h5>
        <button type="button" (click)="dismiss()" [disabled]="saving()" data-dismiss="modal" aria-label="Close" class="btn btn-link text-dark">
          <fa-icon [icon]="icons.faTimes"></fa-icon>
        </button>
      </div>
      <div class="modal-body p-4">
        <app-form-unit [operationsState]="operationsState()" [saving]="saving()" [unit]="unit" (updateUnit)="updateUnit($event)"></app-form-unit>
      </div>
    </ng-template>
  `,
  styleUrls: []
})
export class ModalUnitComponent {
  private _unit: any = null;
  @Input() set unit(value: any) {
    if (value?.id !== this._unit?.id) {
      this.savedUnitPayload = null;
      this.acceptedOperationsStartUnitId = null;
    }
    this._unit = value;
  };

  get unit() {
    return this._unit;
  }
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalUnit') modalUnit !: ElementRef;
  operationsState = input<UnitOperationsState | null>(null);
  saving = signal(false);
  private destroyRef = inject(DestroyRef);
  private savedUnitPayload: string | null = null;
  private acceptedOperationsStartUnitId: number | null = null;

  private modalRef: NgbModalRef | undefined;

  constructor(
    public icons: IconsService,
    private modalService: NgbModal,
    private store: SettlementsService,
    private toastr: ToastrService
  ) { }

  updateUnit(event: any) {
    if (this.saving() || !this.unit?.id) return;
    const { startOperationsDate, ...payload } = event;
    const unitId = Number(this.unit.id);
    const state = this.operationsState();
    const startDate = Number(payload.status) === 1 ? startOperationsDate : '';
    if (startDate && (Number(this.unit.status) !== 1 || state?.unitId !== unitId || state.status !== 'ready')) {
      this.toastr.error('No se puede registrar el inicio de operaciones con el estado actual de la unidad.');
      return;
    }

    const payloadKey = JSON.stringify({ unitId, payload });
    const alreadySaved = this.savedUnitPayload === payloadKey;
    let unitSaved = alreadySaved;
    this.saving.set(true);
    const saveUnit = alreadySaved ? of(null) : this.store.updateUnit(unitId, payload).pipe(tap(() => {
      unitSaved = true;
      this.savedUnitPayload = payloadKey;
    }));
    saveUnit.pipe(
      concatMap(() => startDate ? this.registerOperationsStart(unitId, startDate) : of(null)),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: () => {
        this.toastr.success('Departamento/Local actualizado correctamente');
        this.saving.set(false);
        this.dismiss();
      },
      error: () => {
        this.toastr.error(unitSaved
          ? 'La unidad se guardó, pero no se pudo confirmar el inicio de operaciones. Conserva la fecha y vuelve a intentar.'
          : 'Error al actualizar el departamento/local');
      }
    });
  }

  private registerOperationsStart(unitId: number, startDate: string) {
    return this.store.getUnitOperationsState(unitId).pipe(concatMap(state => {
      if (state.status === 'started' && state.startDate === startDate) return of(state);
      if (state.status !== 'ready' || state.startDate) {
        return throwError(() => new Error('Operations start is not available'));
      }
      if (this.acceptedOperationsStartUnitId === unitId) {
        return throwError(() => new Error('Accepted operations start is not yet confirmed'));
      }
      const request: CalendarEventsRequest = { unit_id: unitId, event_id: 6, event_date: startDate };
      return this.store.saveCalendarEvent(request).pipe(
        tap(() => this.acceptedOperationsStartUnitId = unitId),
        concatMap(() => this.store.getUnitOperationsState(unitId)),
        concatMap(saved => saved.status === 'started' && saved.startDate === startDate
          ? of(saved)
          : throwError(() => new Error('Operations start was not confirmed')))
      );
    }));
  }

  openModal(modalContent: TemplateRef<any>) {
    this.savedUnitPayload = null;
    this.acceptedOperationsStartUnitId = null;
    this.modalRef = this.modalService.open(modalContent, { beforeDismiss: () => !this.saving() });
    this.modalRef.result.then(
      () => this.updateDismiss.emit(),
      () => this.updateDismiss.emit()
    );
  }

  dismiss() {
    if (this.modalRef && !this.saving()) {
      this.modalRef.dismiss();
    }
  }

}
